// Maximum possible ratings for common VA disability conditions
// Based on 38 CFR Schedule for Rating Disabilities
// Last verified: January 2026

export interface ConditionRatingInfo {
  maxRating: number;
  description: string;
  diagnosticCode: string; // Added for reference
  ratingCriteria: {
    rating: number;
    criteria: string;
  }[];
}

export const conditionRatings: { [key: string]: ConditionRatingInfo } = {
  "tinnitus": {
    maxRating: 10,
    description: "Ringing in the ears",
    diagnosticCode: "DC 6260",
    ratingCriteria: [
      { rating: 10, criteria: "Recurrent tinnitus" },
      { rating: 0, criteria: "No compensable tinnitus" }
    ]
  },
  "ptsd": {
    maxRating: 100,
    description: "Post-Traumatic Stress Disorder",
    diagnosticCode: "DC 9411",
    ratingCriteria: [
      { rating: 100, criteria: "Total occupational and social impairment, due to such symptoms as: gross impairment in thought processes or communication; persistent delusions or hallucinations; grossly inappropriate behavior; persistent danger of hurting self or others; intermittent inability to perform activities of daily living (including maintenance of minimal personal hygiene); disorientation to time or place; memory loss for names of close relatives, own occupation, or own name" },
      { rating: 70, criteria: "Occupational and social impairment, with deficiencies in most areas, such as work, school, family relations, judgment, thinking, or mood, due to such symptoms as: suicidal ideation; obsessional rituals which interfere with routine activities; speech intermittently illogical, obscure, or irrelevant; near-continuous panic or depression affecting the ability to function independently, appropriately and effectively; impaired impulse control (such as unprovoked irritability with periods of violence); spatial disorientation; neglect of personal appearance and hygiene; difficulty in adapting to stressful circumstances (including work or a worklike setting); inability to establish and maintain effective relationships" },
      { rating: 50, criteria: "Occupational and social impairment with reduced reliability and productivity due to such symptoms as: flattened affect; circumstantial, circumlocutory, or stereotyped speech; panic attacks more than once a week; difficulty in understanding complex commands; impairment of short- and long-term memory (e.g., retention of only highly learned material, forgetting to complete tasks); impaired judgment; impaired abstract thinking; disturbances of motivation and mood; difficulty in establishing and maintaining effective work and social relationships" },
      { rating: 30, criteria: "Occupational and social impairment with occasional decrease in work efficiency and intermittent periods of inability to perform occupational tasks (although generally functioning satisfactorily, with routine behavior, self-care, and conversation normal), due to such symptoms as: depressed mood, anxiety, suspiciousness, panic attacks (weekly or less often), chronic sleep impairment, mild memory loss (such as forgetting names, directions, recent events)" },
      { rating: 10, criteria: "Occupational and social impairment due to mild or transient symptoms which decrease work efficiency and ability to perform occupational tasks only during periods of significant stress, or symptoms controlled by continuous medication" },
      { rating: 0, criteria: "A mental condition has been formally diagnosed, but symptoms are not severe enough either to interfere with occupational and social functioning or to require continuous medication" }
    ]
  },
  "hearing loss": {
    maxRating: 100,
    description: "Hearing impairment",
    diagnosticCode: "DC 6100",
    ratingCriteria: [
      { rating: 100, criteria: "Anatomical loss of both ears (complete deafness)" },
      { rating: 60, criteria: "Level IX or VIII hearing loss in both ears" },
      { rating: 50, criteria: "Level VIII hearing loss in one ear and Level VII in the other" },
      { rating: 40, criteria: "Level VII hearing loss in both ears" },
      { rating: 30, criteria: "Level VI hearing loss in both ears, or Level VII in one ear and Level VI in the other" },
      { rating: 20, criteria: "Level V hearing loss in both ears, or Level VI in one ear and Level V in the other" },
      { rating: 10, criteria: "Level IV hearing loss in both ears, or various combinations of hearing levels" },
      { rating: 0, criteria: "Hearing loss below compensable levels" }
    ]
  },
  "knee condition": {
    maxRating: 60,
    description: "Knee injuries and conditions",
    diagnosticCode: "DC 5257-5260",
    ratingCriteria: [
      { rating: 60, criteria: "Ankylosis (fusion) of knee in unfavorable position" },
      { rating: 30, criteria: "Recurrent subluxation or lateral instability, severe" },
      { rating: 20, criteria: "Moderate limitation of flexion/extension or moderate instability" },
      { rating: 10, criteria: "Slight limitation of flexion/extension or slight instability" },
      { rating: 0, criteria: "Normal function with no symptoms" }
    ]
  },
  "back condition": {
    maxRating: 100,
    description: "Spinal conditions and back injuries",
    diagnosticCode: "DC 5235-5243",
    ratingCriteria: [
      { rating: 100, criteria: "Unfavorable ankylosis of entire spine" },
      { rating: 50, criteria: "Unfavorable ankylosis of entire thoracolumbar spine, or forward flexion limited to 30 degrees or less; or favorable ankylosis of entire thoracolumbar spine" },
      { rating: 40, criteria: "Unfavorable ankylosis of entire cervical spine; or forward flexion of the thoracolumbar spine limited to 60 degrees or less; or favorable ankylosis of entire cervical spine" },
      { rating: 30, criteria: "Forward flexion of cervical spine limited to 15 degrees or less; or favorable ankylosis of entire thoracolumbar spine" },
      { rating: 20, criteria: "Forward flexion of thoracolumbar spine limited to 85 degrees or less; or forward flexion of cervical spine limited to 30 degrees or less; or muscle spasm or guarding severe enough to result in abnormal gait or abnormal spinal contour" },
      { rating: 10, criteria: "Forward flexion of thoracolumbar spine greater than 85 degrees but not greater than 90 degrees; or forward flexion of cervical spine greater than 30 degrees but not greater than 40 degrees; or combined range of motion of thoracolumbar spine not greater than 120 degrees; or combined range of motion of cervical spine not greater than 170 degrees; or muscle spasm, guarding, or localized tenderness not resulting in abnormal gait or abnormal spinal contour; or vertebral body fracture with loss of 50 percent or more of height" },
      { rating: 0, criteria: "Normal range of motion and no functional impairment" }
    ]
  },
  "shoulder condition": {
    maxRating: 40,
    description: "Shoulder injuries and conditions",
    diagnosticCode: "DC 5200-5203",
    ratingCriteria: [
      { rating: 40, criteria: "Ankylosis of shoulder in unfavorable position, or severe limitation of motion" },
      { rating: 30, criteria: "Moderate limitation of motion (arm can be raised to shoulder level)" },
      { rating: 20, criteria: "Moderate limitation of motion with painful motion" },
      { rating: 10, criteria: "Slight limitation of motion or painful motion" },
      { rating: 0, criteria: "Normal range of motion without symptoms" }
    ]
  },
  "migraine headaches": {
    maxRating: 50,
    description: "Migraine headaches",
    diagnosticCode: "DC 8100",
    ratingCriteria: [
      { rating: 50, criteria: "Very frequent completely prostrating and prolonged attacks productive of severe economic inadaptability" },
      { rating: 30, criteria: "Characteristic prostrating attacks occurring on an average once a month over last several months" },
      { rating: 10, criteria: "Characteristic prostrating attacks averaging one in 2 months over last several months" },
      { rating: 0, criteria: "Less frequent attacks" }
    ]
  },
  "migraines": {
    maxRating: 50,
    description: "Migraine headaches",
    diagnosticCode: "DC 8100",
    ratingCriteria: [
      { rating: 50, criteria: "Very frequent completely prostrating and prolonged attacks productive of severe economic inadaptability" },
      { rating: 30, criteria: "Characteristic prostrating attacks occurring on an average once a month over last several months" },
      { rating: 10, criteria: "Characteristic prostrating attacks averaging one in 2 months over last several months" },
      { rating: 0, criteria: "Less frequent attacks" }
    ]
  },
  "sleep apnea": {
    maxRating: 100,
    description: "Obstructive Sleep Apnea",
    diagnosticCode: "DC 6847",
    ratingCriteria: [
      { rating: 100, criteria: "Chronic respiratory failure with carbon dioxide retention or cor pulmonale, or requires tracheostomy" },
      { rating: 50, criteria: "Requires use of breathing assistance device such as continuous airway pressure (CPAP) machine" },
      { rating: 30, criteria: "Persistent daytime hypersomnolence" },
      { rating: 0, criteria: "Asymptomatic but with documented sleep disorder breathing" }
    ]
  },
  "anxiety disorder": {
    maxRating: 100,
    description: "Generalized Anxiety Disorder",
    diagnosticCode: "DC 9400",
    ratingCriteria: [
      { rating: 100, criteria: "Total occupational and social impairment" },
      { rating: 70, criteria: "Occupational and social impairment, with deficiencies in most areas" },
      { rating: 50, criteria: "Occupational and social impairment with reduced reliability and productivity" },
      { rating: 30, criteria: "Occupational and social impairment with occasional decrease in work efficiency and intermittent periods of inability to perform occupational tasks" },
      { rating: 10, criteria: "Occupational and social impairment due to mild or transient symptoms" },
      { rating: 0, criteria: "A mental condition has been formally diagnosed, but symptoms are not severe enough to interfere with functioning" }
    ]
  },
  "depression": {
    maxRating: 100,
    description: "Major Depressive Disorder",
    diagnosticCode: "DC 9434",
    ratingCriteria: [
      { rating: 100, criteria: "Total occupational and social impairment" },
      { rating: 70, criteria: "Occupational and social impairment, with deficiencies in most areas" },
      { rating: 50, criteria: "Occupational and social impairment with reduced reliability and productivity" },
      { rating: 30, criteria: "Occupational and social impairment with occasional decrease in work efficiency and intermittent periods of inability to perform occupational tasks" },
      { rating: 10, criteria: "Occupational and social impairment due to mild or transient symptoms" },
      { rating: 0, criteria: "A mental condition has been formally diagnosed, but symptoms are not severe enough to interfere with functioning" }
    ]
  },
  "sciatica": {
    maxRating: 80,
    description: "Sciatic nerve paralysis/neuritis",
    diagnosticCode: "DC 8520",
    ratingCriteria: [
      { rating: 80, criteria: "Severe incomplete paralysis with marked muscular atrophy" },
      { rating: 60, criteria: "Moderately severe incomplete paralysis" },
      { rating: 40, criteria: "Moderate incomplete paralysis" },
      { rating: 20, criteria: "Mild incomplete paralysis" },
      { rating: 10, criteria: "Neuritis with mild symptoms such as slight muscular atrophy and/or sensory disturbances" },
      { rating: 0, criteria: "No functional impairment" }
    ]
  },
  "plantar fasciitis": {
    maxRating: 40,
    description: "Plantar fasciitis",
    diagnosticCode: "DC 5276",
    ratingCriteria: [
      { rating: 40, criteria: "Marked deformity with severe pain on use affecting standing and walking" },
      { rating: 30, criteria: "Moderate deformity with moderate pain on use" },
      { rating: 20, criteria: "Mild deformity with mild pain on use" },
      { rating: 10, criteria: "Slight deformity or tenderness with weight bearing" },
      { rating: 0, criteria: "No functional impairment" }
    ]
  },
  "carpal tunnel syndrome": {
    maxRating: 70,
    description: "Carpal Tunnel Syndrome",
    diagnosticCode: "DC 8515",
    ratingCriteria: [
      { rating: 70, criteria: "Severe incomplete paralysis of median nerve, with complete loss of thenar muscle function" },
      { rating: 50, criteria: "Severe incomplete paralysis" },
      { rating: 30, criteria: "Moderate incomplete paralysis" },
      { rating: 20, criteria: "Mild incomplete paralysis" },
      { rating: 10, criteria: "Mild symptoms with sensory disturbances and/or slight motor weakness" },
      { rating: 0, criteria: "No functional impairment" }
    ]
  },
  "hypertension": {
    maxRating: 60,
    description: "Hypertension (High Blood Pressure)",
    diagnosticCode: "DC 7101",
    ratingCriteria: [
      { rating: 60, criteria: "Diastolic pressure predominantly 130 or more" },
      { rating: 40, criteria: "Diastolic pressure predominantly 120 to 129" },
      { rating: 20, criteria: "Diastolic pressure predominantly 110 to 119, or systolic pressure predominantly 200 or more" },
      { rating: 10, criteria: "Diastolic pressure predominantly 100 to 109, or systolic pressure predominantly 160 to 199" },
      { rating: 0, criteria: "Diastolic pressure less than 100 and systolic pressure less than 160" }
    ]
  },
  "diabetes type ii": {
    maxRating: 100,
    description: "Diabetes Mellitus Type II",
    diagnosticCode: "DC 7913",
    ratingCriteria: [
      { rating: 100, criteria: "Requires insulin, restricted diet, plus regulation of activities with episodes of ketoacidosis or hypoglycemic reactions requiring at least three hospitalizations per year or weekly visits to a diabetic care provider, plus progressive loss of weight and strength or complications that would be compensable if separately evaluated" },
      { rating: 60, criteria: "Requires insulin, restricted diet, and regulation of activities" },
      { rating: 40, criteria: "Requires insulin and restricted diet, or oral hypoglycemic agents and restricted diet" },
      { rating: 20, criteria: "Requires insulin or oral hypoglycemic agents, or requires restricted diet" },
      { rating: 10, criteria: "Requires continuous medication" },
      { rating: 0, criteria: "Controlled by diet alone" }
    ]
  }
};

export function getConditionMaxRating(conditionName: string): number {
  const normalizedName = conditionName.toLowerCase().trim();
  const conditionInfo = conditionRatings[normalizedName];
  return conditionInfo?.maxRating || 100; // Default to 100 if condition not found
}

export function getConditionRatingInfo(conditionName: string): ConditionRatingInfo | null {
  const normalizedName = conditionName.toLowerCase().trim();
  return conditionRatings[normalizedName] || null;
}

// Get all valid rating percentages for a condition based on official VA rating criteria
export function getValidRatingsForCondition(conditionName: string): number[] {
  const conditionInfo = getConditionRatingInfo(conditionName);
  if (!conditionInfo) {
    return [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]; // Default ratings
  }
  return conditionInfo.ratingCriteria
    .map(c => c.rating)
    .sort((a, b) => a - b);
}

// Validate that a rating is valid for a given condition
export function isValidRatingForCondition(conditionName: string, rating: number): boolean {
  const validRatings = getValidRatingsForCondition(conditionName);
  return validRatings.includes(rating);
}

// Get the nearest valid rating for a condition if the input rating is invalid
export function getNearestValidRating(conditionName: string, rating: number): number {
  const validRatings = getValidRatingsForCondition(conditionName);
  
  // Find the closest valid rating
  return validRatings.reduce((prev, curr) => 
    Math.abs(curr - rating) < Math.abs(prev - rating) ? curr : prev
  );
}

// Get rating criteria description for a specific rating percentage
export function getRatingCriteriaDescription(conditionName: string, rating: number): string | null {
  const conditionInfo = getConditionRatingInfo(conditionName);
  if (!conditionInfo) return null;
  
  const criteria = conditionInfo.ratingCriteria.find(c => c.rating === rating);
  return criteria?.criteria || null;
}